/**
 * Layout dispatcher — picks the right form for a slide based on
 * ``detectLayout``. Also exposes a tab for the JSON fallback view so
 * editors can drop down to raw markup when needed.
 */

import { useState } from "react";

import type { Slide, SlideUpdatePayload } from "../types";
import { AsksForm } from "./AsksForm";
import { GenericForm } from "./GenericForm";
import { JsonFallbackForm } from "./JsonFallbackForm";
import { OpenerForm } from "./OpenerForm";
import { PathsForm } from "./PathsForm";
import { TwoColForm } from "./TwoColForm";
import { detectLayout, layoutLabel } from "./detectLayout";

type Mode = "layout" | "json";

interface LayoutDispatcherProps {
  draft: Slide;
  setField<K extends keyof SlideUpdatePayload>(
    key: K,
    value: SlideUpdatePayload[K],
  ): void;
  flush(): void;
}

export function LayoutDispatcher({
  draft,
  setField,
  flush,
}: LayoutDispatcherProps) {
  const layout = detectLayout(draft);
  const [mode, setMode] = useState<Mode>("layout");

  return (
    <div data-testid="layout-dispatcher" data-layout={layout}>
      <div style={tabBarStyle} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "layout"}
          onClick={() => setMode("layout")}
          style={tabStyle(mode === "layout")}
          data-testid="layout-tab-form"
        >
          {layoutLabel(layout)} form
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "json"}
          onClick={() => setMode("json")}
          style={tabStyle(mode === "json")}
          data-testid="layout-tab-json"
        >
          JSON
        </button>
      </div>
      {mode === "json" ? (
        <JsonFallbackForm draft={draft} setField={setField} flush={flush} />
      ) : (
        renderForm(layout, draft, setField, flush)
      )}
    </div>
  );
}

function renderForm(
  layout: ReturnType<typeof detectLayout>,
  draft: Slide,
  setField: <K extends keyof SlideUpdatePayload>(
    key: K,
    value: SlideUpdatePayload[K],
  ) => void,
  flush: () => void,
) {
  switch (layout) {
    case "opener":
      return <OpenerForm draft={draft} setField={setField} flush={flush} />;
    case "two-col":
      return <TwoColForm draft={draft} setField={setField} flush={flush} />;
    case "paths":
      return <PathsForm draft={draft} setField={setField} flush={flush} />;
    case "asks":
      return <AsksForm draft={draft} setField={setField} flush={flush} />;
    case "generic":
      return <GenericForm draft={draft} setField={setField} flush={flush} />;
  }
}

const tabBarStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.25rem",
  marginBottom: "0.75rem",
  borderBottom: "1px solid var(--gray-5)",
};

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "0.4rem 0.7rem",
    background: "transparent",
    color: active ? "var(--accent-11)" : "var(--gray-10)",
    border: "none",
    borderBottom: active ? "2px solid var(--accent-9)" : "2px solid transparent",
    cursor: "pointer",
    fontSize: "0.85em",
    fontWeight: active ? 600 : 400,
  };
}
